import React, { useEffect, useState, useRef } from 'react';
import { ShoppingBag, MapPin } from 'lucide-react';
import { scanProducts, pickProduct, type CatalogEntry, type ScannedProduct } from '../lib/productScanner';
import { ARGENTINE_NAMES, ARGENTINE_PROVINCES } from '../lib/argentineData';
interface ToastData {
  id: number;
  name: string;
  province: string;
  product: ScannedProduct;
}
const FIRST_DELAY = 3000;
const MIN_INTERVAL = 8000;
const MAX_INTERVAL = 15000;
const VISIBLE_DURATION = 4000;
const NAME_HISTORY_SIZE = 10;
function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function pickNameAvoiding(history: string[]): string {
  const available = ARGENTINE_NAMES.filter(n => !history.includes(n));
  if (available.length === 0) return pickRandom(ARGENTINE_NAMES);
  return pickRandom(available);
}
export function PurchaseToast() {
  const [toast, setToast] = useState<ToastData | null>(null);
  const [isExiting, setIsExiting] = useState(false);
  const catalogRef = useRef<CatalogEntry[]>([]);
  const nameHistoryRef = useRef<string[]>([]);
  const timeoutsRef = useRef<number[]>([]);
  const idCounterRef = useRef(0);
  const reduceMotionRef = useRef(false);
  useEffect(() => {
    // Respect prefers-reduced-motion
    if (typeof window !== 'undefined' && window.matchMedia) {
      reduceMotionRef.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    // Scan the DOM after it has had a chance to settle
    const scanDelay = window.setTimeout(() => {
      catalogRef.current = scanProducts();
    }, 100);
    timeoutsRef.current.push(scanDelay);
    const showOne = () => {
      // Build toast data
      const name = pickNameAvoiding(nameHistoryRef.current);
      nameHistoryRef.current = [name, ...nameHistoryRef.current].slice(0, NAME_HISTORY_SIZE);
      const province = pickRandom(ARGENTINE_PROVINCES);
      const product = pickProduct(catalogRef.current.length > 0 ? catalogRef.current : scanProducts());
      idCounterRef.current += 1;
      setIsExiting(false);
      setToast({
        id: idCounterRef.current,
        name,
        province,
        product
      });
      // Trigger exit animation just before unmount
      const exitTimer = window.setTimeout(() => {
        setIsExiting(true);
      }, VISIBLE_DURATION);
      timeoutsRef.current.push(exitTimer);
      // Remove from DOM after the slide-out finishes
      const removeTimer = window.setTimeout(() => {
        setToast(null);
        // Schedule the next one
        const nextDelay = randInt(MIN_INTERVAL, MAX_INTERVAL);
        const nextTimer = window.setTimeout(showOne, nextDelay);
        timeoutsRef.current.push(nextTimer);
      }, VISIBLE_DURATION + 500);
      timeoutsRef.current.push(removeTimer);
    };
    // First toast after FIRST_DELAY
    const firstTimer = window.setTimeout(showOne, FIRST_DELAY);
    timeoutsRef.current.push(firstTimer);
    return () => {
      timeoutsRef.current.forEach(t => window.clearTimeout(t));
      timeoutsRef.current = [];
    };
  }, []);
  if (!toast) return null;
  const animationClass = reduceMotionRef.current ? '' : isExiting ? 'animate-toast-out' : 'animate-toast-in';
  return <div role="status" aria-live="polite" className={`
        fixed bottom-24 md:bottom-6 left-1/2 -translate-x-1/2 z-[90]
        w-[calc(100%-2rem)] max-w-sm
        bg-white rounded-2xl shadow-premium border border-sand-200
        px-3 py-3 flex items-center gap-3
        ${animationClass}
      `} data-id="element-279">
      {/* Thumbnail */}
      <div className="flex-shrink-0 w-14 h-14 bg-sand-50 rounded-xl overflow-hidden border border-sand-100 flex items-center justify-center" data-id="element-280">
        {toast.product.thumbnailUrl ? <img src={toast.product.thumbnailUrl} alt={toast.product.title} className="w-full h-full object-contain p-1" data-id="element-281" /> : <ShoppingBag className="w-6 h-6 text-concrete-400" data-id="element-282" />}
      </div>

      {/* Copy */}
      <div className="flex-1 min-w-0" data-id="element-283">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-sage-600 font-bold mb-0.5" data-id="element-284">
          <span className="relative flex h-1.5 w-1.5" data-id="element-285">
            <span className="absolute inline-flex h-full w-full rounded-full bg-sage-500 opacity-75 animate-ping" data-id="element-286" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-sage-600" data-id="element-287" />
          </span>
          Compra verificada
        </div>
        <p className="text-sm text-concrete-800 font-medium leading-snug truncate" data-id="element-288">
          <span className="font-semibold" data-id="element-289">{toast.name}</span> en{' '}
          <span className="font-semibold" data-id="element-290">{toast.province}</span>
        </p>
        <p className="text-xs text-concrete-500 font-light truncate flex items-center gap-1" data-id="element-291">
          <MapPin className="w-3 h-3 text-gold-500 flex-shrink-0" data-id="element-292" />
          <span className="truncate" data-id="element-293">
            {toast.product.editionLabel || toast.product.title} —{' '}
            <span className="text-terracotta-600 font-medium" data-id="element-294">
              {toast.product.priceText}
            </span>
          </span>
        </p>
      </div>
    </div>;
}